import { useContext, useMemo } from 'react';
import { readSearchParam } from 'src/common/readSearchParam.ts';
import { LOCATION_STORE_PARAM_NAME } from 'src/common/constants.ts';
import {
    initLocationState as initValveLocationState,
    isLocationState as isValveLocationState,
} from 'src/modules/locationStateContext.ts';
import { ConfigContext } from 'src/configContext.ts';
import { usePriceQuery } from 'src/projects/hooks/usePriceQuery.ts';
import {
    FetchValveSelectionDocument,
    FetchValveSelectionQuery,
    FetchValveSelectionQueryVariables,
} from 'src/generated/operations.ts';
import { SelectionDataValue } from 'src/projects/components/SelectionDataContext.ts';
import { safeTotal } from 'src/projects/utils/SafeTotal.ts';
import { calculateTotals as calculateValveTotals } from 'src/modules/checkout/CalculateTotals.ts';
import { removeDuplicateFragments } from 'src/utils/de-dupe-fragments.ts';
import { Provide, ProviderProps } from 'src/projects/components/SelectionDataProvider.tsx';

const FetchValveSelectionDedupedDocument = removeDuplicateFragments(FetchValveSelectionDocument);

export const ValveProvider = ({ selection, children }: ProviderProps) => {
    const locationState = useMemo(() => readSearchParam(
        new URLSearchParams(selection.searchQuery),
        LOCATION_STORE_PARAM_NAME,
        { ...initValveLocationState },
        isValveLocationState,
    ), [selection.searchQuery]);

    const { userSettings, currency } = useContext(ConfigContext);

    const variables = useMemo(() => ({
        actuatorLabel: locationState.actuatorLabel,
        valveLabel: locationState.valveLabel,
        locale: userSettings.currentLocale,
        region: userSettings.currentRegion,
    }), [locationState, userSettings]);

    const { data } = usePriceQuery<
    FetchValveSelectionQuery,
    FetchValveSelectionQueryVariables
    >(FetchValveSelectionDedupedDocument, {
        variables,
    });

    const value = useMemo<SelectionDataValue>(() => {
        if (data === undefined || locationState === null) {
            return { loading: true, total: null, currency: currency ?? 'USD', data: undefined };
        }
        const comboCurrent = locationState.combo ?? {
            valve: locationState.valveLabel,
            actuator: locationState.actuatorLabel,
            mounted: false,
            qty: 1,
        };
        const comboCandidate = data.vssCombo.find((item) => item.Label.includes(comboCurrent.mounted ? '+' : '/'));
        const combo = comboCandidate ?? data.vssAccessories.valve;
        const { total, currency: cur } = safeTotal(
            () => calculateValveTotals(
                combo,
                data.vssAccessories,
                userSettings,
                { ...locationState, combo: comboCurrent },
                currency,
                locationState.pipeConnectors,
            ),
            currency ?? 'USD',
        );
        return { loading: false, total, currency: cur, data };
    }, [currency, data, locationState, userSettings]);

    return <Provide value={value}>{children}</Provide>;
};
